import { isEqual } from "lodash";
import { DateTime } from "luxon";
import React, { ComponentProps, useState } from "react";
import { common_utils } from "../../../../../common/common_utils";
import { Constants } from "../../../../../common/constants";
import {
	SeshThreadAutoArchiveDuration,
	ThreadSettings,
} from "../../../../../common/sesh_types";
import { DashboardUtils, RevertDescriptor } from "../../dashboard_utils";
import { AutocompleteInput } from "./autocomplete_input/autocomplete_input";
import { ModifySettingsButton } from "./base_components/modify_settings_button";
import { SeshAddonInput } from "./base_components/sesh_addon_input";
import { SeshSelect } from "./base_components/sesh_select_input";
import { SeshTextInput } from "./base_components/sesh_text_input";
import { ErrorNote } from "./error_note";
import { SeshAccordianComponent } from "./sesh_accordian_component/sesh_accordian_component";
import { InfoTooltip } from "./tooltips/info_tooltip";

interface BaseProps {
	event_name?: string;
	start_time?: Date;
	guild_timezone?: string;
	value: ThreadSettings;
	onUpdate: (thread_settings: ThreadSettings) => void;
	disabled?: boolean;
}

function getEphemeralValue(
	value: ThreadSettings,
	default_value: ThreadSettings
) {
	return (
		value ??
		default_value ?? {
			name: Constants.EVENT_NAME_PLACEHOLDER,
			auto_archive_duration: SeshThreadAutoArchiveDuration.OneDay,
			should_auto_join_attendees_on_rsvp: true,
			should_send_start_message_to_thread: true,
		}
	);
}

export function EventThreadAccordian(
	props: BaseProps & {
		is_loading?: boolean;
		is_unsaved?: boolean;
		error?: string | JSX.Element;

		revert_descriptor?: RevertDescriptor<ThreadSettings>;

		type: "EventCreation" | "GuildSetting";

		/**
		 * This field should be combined into overridden_state, but then we'd have to convert everything else to use that as well
		 *
		 * Just have an extra type in the `type` field "ChannelOverride" and then use overridden state to determine overridden status
		 */
		is_channel_override_active?: boolean;

		show_on_closed?: boolean;
	}
) {
	const [is_open, setIsOpen] = useState(!!props.value);
	const [ephemeral_value, setEphemeralValue] = useState<ThreadSettings>(
		getEphemeralValue(props.value, props.revert_descriptor?.default?.value)
	);

	if (!props.error && !props.value && is_open) {
		setIsOpen(false);
	}

	const label =
		props.type === "EventCreation"
			? "Create Thread for Event"
			: "Create Thread for Events";

	const info =
		props.type === "EventCreation"
			? "Create a thread for this event as soon as it is created"
			: "Create a thread for every event";

	const error_note = props.error ? <ErrorNote error={props.error} /> : null;

	return (
		<SeshAccordianComponent
			is_opened={is_open}
			label={label}
			info={info}
			is_beta
			is_unsaved={props.is_unsaved}
			is_loading={props.is_loading}
			is_channel_override_active={props.is_channel_override_active}
			is_new={true}
			revert_descriptor={
				props.revert_descriptor
					? {
							...props.revert_descriptor,
							onRevertPressed: () => {
								props.revert_descriptor.onRevertPressed();
								setEphemeralValue(
									getEphemeralValue(
										undefined,
										props.revert_descriptor?.default?.value
									)
								);
								setIsOpen(
									!!props.revert_descriptor.default.value
								);
							},
					  }
					: undefined
			}
			onChange={(isOpen) => {
				setIsOpen(isOpen);
				if (props.error) {
					props.onUpdate(undefined);
					return;
				}

				if (!isOpen) {
					props.onUpdate(undefined);
				} else {
					props.onUpdate(ephemeral_value);
				}
			}}
			disabled={props.disabled}
			closed_element={
				props.show_on_closed ? (
					<div className="cursor-not-allowed opacity-25">
						<div className="disable-pointer-events">
							<EventThreadDisplay
								event_name={props.event_name}
								start_time={props.start_time}
								guild_timezone={props.guild_timezone}
								value={ephemeral_value}
								onUpdate={(val) => {}}
								disabled={true}
							/>
						</div>
					</div>
				) : undefined
			}
		>
			{props.error ? (
				error_note
			) : (
				<EventThreadDisplay
					event_name={props.event_name}
					start_time={props.start_time}
					guild_timezone={props.guild_timezone}
					value={props.value}
					onUpdate={(val) => {
						if (!is_open) return;
						setEphemeralValue(val);
						props.onUpdate(val);
					}}
					disabled={props.disabled}
				/>
			)}
		</SeshAccordianComponent>
	);
}

export function EventThreadDisplay(props: BaseProps) {
	const [is_modal_open, setIsModalOpen] = useState(false);

	const start_time = props.start_time
		? DateTime.fromJSDate(props.start_time)
		: undefined;

	const parsed_event_name = common_utils.replacePlaceholders(
		"ThreadTitle",
		props.value?.name ?? "",
		{
			event_name: props.event_name,
			event_link: undefined,
			start_time: start_time,
			timezone: props.guild_timezone,
			duration_ms: start_time?.diffNow().milliseconds,
			rsvp_options: undefined,
		}
	);

	return (
		<div className="flex flex-col">
			<div className="flex flex-wrap gap-4">
				<div className="">
					<div className="text-sesh-dark">Thread Title</div>
					<div title={parsed_event_name}>
						{DashboardUtils.LimitName(parsed_event_name, 16)}
					</div>
				</div>

				<div className="">
					<div className="text-sesh-dark">Add on RSVP</div>
					<div>
						{props.value.should_auto_join_attendees_on_rsvp
							? "Enabled"
							: "Disabled"}
					</div>
				</div>
				<div className="">
					<div className="text-sesh-dark">Event Start Message</div>
					<div>
						{props.value.should_send_start_message_to_thread
							? "Thread"
							: "Channel"}
					</div>
				</div>
				<div className="">
					<div className="text-sesh-dark">Archive Time</div>
					<div>
						{
							AutoArchiveDurationToLabel[
								props.value.auto_archive_duration
							]
						}
					</div>
				</div>
			</div>
			<ModifySettingsButton
				className="mt-4"
				disabled={props.disabled}
				onClick={(e) => {
					e.stopPropagation();
					e.preventDefault();

					if (props.disabled) {
						return;
					}

					setIsModalOpen(true);
				}}
			>
				Modify
			</ModifySettingsButton>
			{is_modal_open && (
				<EventThreadModal
					{...props}
					closeModal={() => setIsModalOpen(false)}
				/>
			)}
		</div>
	);
}

function EventThreadModal(props: BaseProps & { closeModal: () => void }) {
	const [state, setState] = useState(props.value);

	return (
		<DashboardUtils.SeshModal
			is_active={true}
			onClose={(e) => {
				e.stopPropagation();
				e.preventDefault();
				props.closeModal();
			}}
			title={"Event Thread"}
			//subtitle={subtitle}
			content={
				<div className="event-thread-modal">
					<div className="mb-4">
						<EventThreadTitleInput
							value={state.name}
							onUpdate={(name) =>
								setState({
									...state,
									name: name,
								})
							}
						/>
					</div>
					<div className="mb-4">
						<AutoJoinAttendeesInput
							value={state.should_auto_join_attendees_on_rsvp}
							onUpdate={(val) =>
								setState({
									...state,
									should_auto_join_attendees_on_rsvp: val,
								})
							}
						/>
					</div>
					<div className="mb-4">
						<SendEventStartMessageToThreadInput
							value={state.should_send_start_message_to_thread}
							onUpdate={(val) =>
								setState({
									...state,
									should_send_start_message_to_thread: val,
								})
							}
						/>
					</div>
					<div className="mb-4">
						<EventThreadDurationInput
							value={state.auto_archive_duration}
							onUpdate={(val) =>
								setState({
									...state,
									auto_archive_duration: val,
								})
							}
						/>
					</div>
					<div className="is-pulled-right mt-4">
						<button
							className={`button dashboard-primary-button--tiny is-size-6 has-text-right`}
							type="button"
							disabled={state.name.length === 0}
							onClick={() => {
								props.onUpdate(state);
								props.closeModal();
							}}
						>
							<span className="has-text-weight-bold">
								Save Thread Settings
							</span>
						</button>
					</div>
				</div>
			}
		/>
	);
}

const AutoArchiveDurationToLabel: Record<
	SeshThreadAutoArchiveDuration,
	string
> = {
	[SeshThreadAutoArchiveDuration.OneDay]: "One Day",
	[SeshThreadAutoArchiveDuration.OneHour]: "One Hour",
	[SeshThreadAutoArchiveDuration.OneWeek]: "One Week",
	[SeshThreadAutoArchiveDuration.ThreeDays]: "Three Days",
};

function EventThreadDurationInput(props: {
	value: ThreadSettings["auto_archive_duration"];
	onUpdate: (duration: ThreadSettings["auto_archive_duration"]) => void;
}) {
	const options: { label: string; value: SeshThreadAutoArchiveDuration }[] =
		[];

	// Remove numbers from keys
	const keys = Object.keys(SeshThreadAutoArchiveDuration).filter((val) =>
		Number.isNaN(Number(val))
	);
	for (const archive_duration of keys) {
		const casted_key =
			archive_duration as keyof typeof SeshThreadAutoArchiveDuration;

		options.push({
			label: AutoArchiveDurationToLabel[
				SeshThreadAutoArchiveDuration[casted_key]
			],
			value: SeshThreadAutoArchiveDuration[casted_key],
		});
	}

	const value = options.find((val) => val.value === props.value);

	return (
		<SeshSelect
			label="Auto Archive Duration"
			options={options}
			value={value}
			onChange={(val) => props.onUpdate(val.value)}
		/>
	);
}

function AutoJoinAttendeesInput(props: {
	value: ThreadSettings["should_auto_join_attendees_on_rsvp"];
	onUpdate: (
		name: ThreadSettings["should_auto_join_attendees_on_rsvp"]
	) => void;
}) {
	return (
		<SeshAddonInput
			label={
				<div className="flex items-center gap-2">
					Add Attendees on RSVP
					<InfoTooltip
						info={
							"If enabled, users who RSVP to an 'Is Attendee' role will be automatically invited to the thread. To modify what RSVP option is considered an attendee or not, go to the 'RSVP Options' section."
						}
					></InfoTooltip>
				</div>
			}
			value={props.value}
			setValue={props.onUpdate}
			buttons={[
				{
					label: "Enabled",
					value: true,
				},
				{
					label: "Disabled",
					value: false,
				},
			]}
		/>
	);
}

function SendEventStartMessageToThreadInput(props: {
	value: ThreadSettings["should_send_start_message_to_thread"];
	onUpdate: (
		name: ThreadSettings["should_send_start_message_to_thread"]
	) => void;
}) {
	return (
		<SeshAddonInput
			label="Event Notification Destination"
			value={props.value}
			setValue={props.onUpdate}
			buttons={[
				{
					label: "Thread",
					value: true,
				},
				{
					label: "Channel",
					value: false,
				},
			]}
		/>
	);
}

function EventThreadTitleInput(props: {
	event_name?: string;
	value: ThreadSettings["name"];
	onUpdate: (name: ThreadSettings["name"]) => void;
}) {
	return (
		<SeshTextInput
			label="Thread Title"
			input_element_override={
				<AutocompleteInput
					placeholder="Title"
					value={props.value}
					onChange={(val) => {
						props.onUpdate(val);
					}}
					type="title"
					char_limit={Constants.THREAD_TITLE_LIMIT}
				/>
			}
			input_props={{}}
			help_text={
				<div className="flex">
					<div className="">
						Type <span className="command-example--inline">$</span>{" "}
						for a full list of{" "}
						<a
							className="manual-link"
							href={
								DashboardUtils.GetManualLink() + `/placeholders`
							}
							target="_blank"
						>
							placeholders
						</a>
						.
					</div>
				</div>
			}
			error_text={
				props.value.length === 0 ? "Must not be empty" : undefined
			}
		/>
	);
}
